import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useTheme } from '../theme/ThemeContext';
import { X, FileText } from 'lucide-react';
import { sendGoogleAdsConversion } from '../utils/googleAds';

const FORMSPREE_ENDPOINT = 'https://formspree.io/f/myknggva';

export default function ReportIntakeModal({ open, onClose }) {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [lineOfBusiness, setLineOfBusiness] = useState('');
  const [targetMarket, setTargetMarket] = useState('');
  const [locations, setLocations] = useState('');
  const [competitorMode, setCompetitorMode] = useState('find'); // 'find' | 'specific' | 'both'
  const [knownCompetitors, setKnownCompetitors] = useState('');
  const [additionalDetails, setAdditionalDetails] = useState('');
  const [status, setStatus] = useState(''); // '' | 'sending' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const reset = () => {
    setLineOfBusiness('');
    setTargetMarket('');
    setLocations('');
    setCompetitorMode('find');
    setKnownCompetitors('');
    setAdditionalDetails('');
    setStatus('');
    setErrorMessage('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setErrorMessage('');

    const competitorText =
      competitorMode === 'specific'
        ? `Client provided specific competitors: ${knownCompetitors}`
        : competitorMode === 'find'
        ? 'Please find competitors for client'
        : `Find more competitors AND client has some: ${knownCompetitors}`;

    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          _subject: 'Single Intelligence Report Request ($79)',
          'Line of Business': lineOfBusiness,
          'Target Market': targetMarket,
          'Locations of Interest': locations,
          'Competitor Requirements': competitorText,
          'Additional Details': additionalDetails || '(None provided)',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && !data.error) {
        sendGoogleAdsConversion('reportLead', { dedupeKey: `report:${lineOfBusiness}:${targetMarket}` });
        setStatus('success');
      } else {
        setStatus('error');
        setErrorMessage(data.error || data.errors?.map((e) => e.message).join(' ') || 'Something went wrong.');
      }
    } catch (err) {
      setStatus('error');
      setErrorMessage(err.message || 'Failed to send. Please try again.');
    }
  };

  const handleContinueToPayment = () => {
    handleClose();
    navigate('/checkout?plan=single_int_report');
  };

  if (!open) return null;

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    fontSize: 14,
    border: `1px solid ${theme.inputBorder}`,
    borderRadius: 8,
    backgroundColor: theme.inputBg || theme.card,
    color: theme.text,
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    outline: 'none',
  };

  const labelStyle = {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: theme.text,
    marginBottom: 4,
  };

  const fieldStyle = { marginBottom: 16 };

  const required = <span style={{ color: '#f43f5e', marginLeft: 2 }}>*</span>;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="report-intake-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        backgroundColor: 'rgba(0,0,0,0.55)',
        overflowY: 'auto',
      }}
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 560,
          backgroundColor: theme.card,
          borderRadius: 16,
          border: `1px solid ${theme.border}`,
          boxShadow: theme.shadow,
          padding: 28,
          maxHeight: '90vh',
          overflowY: 'auto',
          margin: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <FileText size={20} color="#06b6d4" />
              <h2 id="report-intake-title" style={{ margin: 0, fontSize: 20, fontWeight: 700, color: theme.text }}>
                Intelligence Report Request
              </h2>
            </div>
            <p style={{ margin: 0, fontSize: 13, color: theme.textMuted }}>
              Tell us about your market so we can tailor your report.
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            style={{
              padding: 6,
              border: 'none',
              background: 'transparent',
              color: theme.textMuted,
              cursor: 'pointer',
              borderRadius: 8,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={22} />
          </button>
        </div>

        <div style={{ width: '100%', height: 1, backgroundColor: theme.border, margin: '16px 0' }} />

        {status === 'success' ? (
          <div style={{ padding: '16px 0 8px', textAlign: 'center' }}>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                backgroundColor: 'rgba(6,182,212,0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}
            >
              <FileText size={26} color="#06b6d4" />
            </div>
            <h3 style={{ margin: '0 0 8px', color: theme.text, fontSize: 18, fontWeight: 700 }}>
              Requirements received!
            </h3>
            <p style={{ margin: '0 0 24px', color: theme.textMuted, fontSize: 14, lineHeight: 1.6, maxWidth: 400, marginLeft: 'auto', marginRight: 'auto' }}>
              We have your report details. Complete your payment and we'll start on your custom market intelligence report right away.
            </p>
            {isAuthenticated ? (
              <button
                type="button"
                onClick={handleContinueToPayment}
                style={{
                  padding: '12px 32px',
                  fontSize: 15,
                  fontWeight: 600,
                  color: '#fff',
                  backgroundColor: '#06b6d4',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                }}
              >
                Continue to Payment →
              </button>
            ) : (
              <div>
                <p style={{ margin: '0 0 14px', fontSize: 13, color: theme.textMuted }}>
                  Sign in or create a free account to complete your payment.
                </p>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => { handleClose(); navigate('/login'); }}
                    style={{
                      padding: '10px 24px',
                      fontSize: 14,
                      fontWeight: 600,
                      color: theme.text,
                      backgroundColor: 'transparent',
                      border: `1px solid ${theme.border}`,
                      borderRadius: 8,
                      cursor: 'pointer',
                    }}
                  >
                    Log in
                  </button>
                  <button
                    type="button"
                    onClick={() => { handleClose(); navigate('/signup'); }}
                    style={{
                      padding: '10px 24px',
                      fontSize: 14,
                      fontWeight: 600,
                      color: '#fff',
                      backgroundColor: '#06b6d4',
                      border: 'none',
                      borderRadius: 8,
                      cursor: 'pointer',
                    }}
                  >
                    Create account →
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* Line of business */}
            <div style={fieldStyle}>
              <label htmlFor="ri-lob" style={labelStyle}>
                Line of Business{required}
              </label>
              <input
                id="ri-lob"
                type="text"
                value={lineOfBusiness}
                onChange={(e) => setLineOfBusiness(e.target.value)}
                placeholder="e.g. Commercial cleaning, SaaS, Food service, Retail"
                required
                style={inputStyle}
              />
            </div>

            {/* Target market */}
            <div style={fieldStyle}>
              <label htmlFor="ri-target" style={labelStyle}>
                Target Market{required}
              </label>
              <input
                id="ri-target"
                type="text"
                value={targetMarket}
                onChange={(e) => setTargetMarket(e.target.value)}
                placeholder="e.g. Small businesses, Homeowners, Enterprise companies"
                required
                style={inputStyle}
              />
            </div>

            {/* Locations */}
            <div style={fieldStyle}>
              <label htmlFor="ri-locations" style={labelStyle}>
                Locations of Interest{required}
              </label>
              <input
                id="ri-locations"
                type="text"
                value={locations}
                onChange={(e) => setLocations(e.target.value)}
                placeholder="e.g. New York City, Dallas TX, Southeast United States"
                required
                style={inputStyle}
              />
              <p style={{ margin: '5px 0 0', fontSize: 12, color: theme.textMuted }}>
                List any cities, regions, or states you want us to focus on.
              </p>
            </div>

            {/* Competitor mode */}
            <div style={fieldStyle}>
              <label style={labelStyle}>
                Competitor Analysis{required}
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { value: 'find', label: 'Find competitors for me' },
                  { value: 'specific', label: 'I have specific competitors to analyze' },
                  { value: 'both', label: 'Both — find more and I have some to include' },
                ].map(({ value, label }) => (
                  <label
                    key={value}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 14px',
                      border: `1px solid ${competitorMode === value ? '#06b6d4' : theme.border}`,
                      borderRadius: 8,
                      cursor: 'pointer',
                      backgroundColor: competitorMode === value ? 'rgba(6,182,212,0.08)' : 'transparent',
                      transition: 'border-color 0.15s, background-color 0.15s',
                    }}
                  >
                    <input
                      type="radio"
                      name="ri-competitor-mode"
                      value={value}
                      checked={competitorMode === value}
                      onChange={() => setCompetitorMode(value)}
                      style={{ accentColor: '#06b6d4', width: 16, height: 16, flexShrink: 0 }}
                    />
                    <span style={{ fontSize: 14, color: theme.text }}>{label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Known competitors — shown only if specific or both */}
            {(competitorMode === 'specific' || competitorMode === 'both') && (
              <div style={fieldStyle}>
                <label htmlFor="ri-competitors" style={labelStyle}>
                  Known Competitors{required}
                </label>
                <textarea
                  id="ri-competitors"
                  value={knownCompetitors}
                  onChange={(e) => setKnownCompetitors(e.target.value)}
                  placeholder="List competitor names, one per line or separated by commas"
                  required
                  rows={3}
                  style={{ ...inputStyle, resize: 'vertical' }}
                />
              </div>
            )}

            {/* Additional details */}
            <div style={fieldStyle}>
              <label htmlFor="ri-details" style={labelStyle}>
                Additional Details
              </label>
              <textarea
                id="ri-details"
                value={additionalDetails}
                onChange={(e) => setAdditionalDetails(e.target.value)}
                placeholder="Any other context, specific questions, or focus areas for your report…"
                rows={3}
                style={{ ...inputStyle, resize: 'vertical' }}
              />
            </div>

            {status === 'error' && errorMessage && (
              <p style={{ margin: '0 0 12px', fontSize: 13, color: '#f43f5e' }}>
                {errorMessage}
              </p>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={handleClose}
                style={{
                  padding: '10px 18px',
                  fontSize: 14,
                  fontWeight: 600,
                  color: theme.textMuted,
                  backgroundColor: 'transparent',
                  border: `1px solid ${theme.border}`,
                  borderRadius: 8,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={status === 'sending'}
                style={{
                  padding: '10px 24px',
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#fff',
                  backgroundColor: '#06b6d4',
                  border: 'none',
                  borderRadius: 8,
                  cursor: status === 'sending' ? 'wait' : 'pointer',
                  opacity: status === 'sending' ? 0.75 : 1,
                }}
              >
                {status === 'sending' ? 'Submitting…' : 'Submit & Continue →'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
